import { AfterViewInit, Component, OnDestroy, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { NbRoleProvider } from '@nebular/security';
import { Subject } from 'rxjs';
import { mergeMap, takeUntil } from 'rxjs/operators';
import { ApplicationsService } from '~core/data/applications.service';
import { Application } from '~core/models/application.model';
import { Roles } from '~core/models/roles.model';
import { ApplicationEditDialogComponent } from './application-edit/application-edit-dialog.component';

@Component({
  selector: 'ngx-applications',
  templateUrl: './applications.component.html',
  styleUrls: ['./applications.component.scss']
})
export class ApplicationsComponent implements AfterViewInit, OnDestroy {
  private unsubscribe$ = new Subject<void>();

  displayedColumns: string[] = ['name', 'description'];

  dataSource = new MatTableDataSource<Application>();
  filteredApplications$ = new Subject<Application[]>();
  applications: Application[];

  @ViewChild(MatSort, { static: true }) sort: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

  showHidden = false;

  constructor(
    private _appsService: ApplicationsService,
    private _dialog: MatDialog,
    private router: Router,
    private roleProvider: NbRoleProvider
  ) {
    this.dataSource = new MatTableDataSource();

    this.dataSource.filterPredicate = (data: Application, filters: string) => {
      const matchFilter = [];
      // will split by comma and upto 1 space
      const filterArray = filters.split(/,\s?/);

      // const columns = [data.name, data.race, data.color];
      // Or if you don't want to specify specifics columns =>
      // const columns = (<any>Object).values(data);

      // Main loop
      filterArray.forEach(filter => {
        const customFilter = [];

        // Get only viewed columns from data
        // Assumes view isn't displaying more trhen 1 data field
        this.displayedColumns.forEach(columnName => {
          const column = data[columnName];
          if (column) {
            customFilter.push(
              column
                .toString()
                .toLowerCase()
                .includes(filter)
            );
          }
        });

        matchFilter.push(customFilter.some(Boolean)); // OR
      });
      return matchFilter.every(Boolean); // AND
    };

    this.filteredApplications$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(apps => {
        this.dataSource.data = apps;
      });
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;

    /* now it's okay to set large data source... */
    this.roleProvider
      .getRole()
      .pipe(
        mergeMap(response => {
          let roles: string[];
          // Admin and SU pass no roles to api
          if (
            response.includes(Roles[Roles.Administrator]) ||
            response.includes(Roles[Roles.SuperUser])
          ) {
            roles = null;
          } else {
            roles = [Roles[Roles.Manager], Roles[Roles.Developer]];
          }

          return this._appsService.list(roles);
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(data => {
        // console.log(data);
        this.applications = data.sort((a, b) =>
          a.name > b.name ? 1 : b.name > a.name ? -1 : 0
        );
        this.filteredApplications$.next(this.globalFilter());
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  setHiddenFilter(event) {
    this.showHidden = event.checked;

    this.filteredApplications$.next(this.globalFilter());
  }

  globalFilter(): Application[] {
    if (!this.showHidden) {
      const apps = this.applications.filter(
        app => app.hidden === undefined || app.hidden === false
      );
      return apps;
    } else {
      return this.applications;
    }
  }

  applyFilter(filterValue: string) {
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  onEditClick(event): void {
    this.router.navigate(['/applications', event.applicationId]);
  }

  addApp() {
    const application = new Application();
    application.applicationId = 0;

    const dlg = this._dialog.open(ApplicationEditDialogComponent, {
      minWidth: '400px',
      data: application
    });

    dlg.afterClosed().subscribe(app => {
      if (app) {
        this.dataSource.data.push(app);
        this.dataSource.data = this.dataSource.data;
      }
    });
  }
}
